import type { ElementType, HTMLAttributes } from "react";
import APP_COPY from "./app-copy.json";

/**
 * App and onboarding copy (the prototype's APP_COPY, docs/COPY-CONFIG.md). Every key is
 * `page.section.element`. Values are authored, trusted strings and may contain inline markup
 * (`<em>`, `<a>`, `&rsquo;`), so <Copy> renders them as HTML. Never put user input into a value.
 *
 * `{name}` placeholders are filled from `params`; parameter values are escaped.
 */
type Leaves<T, P extends string = ""> = {
  [K in keyof T & string]: T[K] extends string
    ? `${P}${K}`
    : T[K] extends object
      ? Leaves<T[K], `${P}${K}.`>
      : never;
}[keyof T & string];

export type CopyKey = Leaves<typeof APP_COPY>;
export type CopyParams = Record<string, string | number>;

const ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ESCAPES[c]);
}

function lookup(path: string): unknown {
  return path.split(".").reduce<unknown>((node, key) => {
    if (node && typeof node === "object" && key in node) {
      return (node as Record<string, unknown>)[key];
    }
    return undefined;
  }, APP_COPY);
}

/** The raw (HTML) value of a copy key, with `{params}` filled in (escaped). */
export function copy(key: CopyKey, params?: CopyParams): string {
  const value = lookup(key);
  if (typeof value !== "string") {
    throw new Error(`Missing copy key: ${key}`);
  }
  if (!params) return value;
  return value.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? escapeHtml(String(params[name])) : match,
  );
}

/** The copy value as plain text (markup and entities removed), e.g. for attributes and titles. */
export function copyText(key: CopyKey, params?: CopyParams): string {
  const html = copy(key, params);
  if (typeof document === "undefined") return html.replace(/<[^>]*>/g, "");
  const el = document.createElement("div");
  el.innerHTML = html;
  return el.textContent ?? "";
}

type CopyProps = {
  k: CopyKey;
  params?: CopyParams;
  as?: ElementType;
} & Omit<HTMLAttributes<HTMLElement>, "children" | "dangerouslySetInnerHTML">;

/** Renders a copy value. The prototype's `data-a="key"` becomes `<Copy k="key" />`. */
export function Copy({ k, params, as: Tag = "span", ...rest }: CopyProps) {
  return <Tag data-a={k} {...rest} dangerouslySetInnerHTML={{ __html: copy(k, params) }} />;
}
